import { useStorage } from '@vueuse/core'
import { computed, shallowRef } from 'vue'

import { isWorkerSessionToken, loginToWorker, type WorkerLoginResponse } from '@/services/workerApi'

const STORAGE_KEY = 'hanoi-bus:worker-session-v1'

const tokenSerializer = {
  read(raw: string): string | null {
    if (isWorkerSessionToken(raw)) return raw
    try {
      window.localStorage.removeItem(STORAGE_KEY)
    } catch {
      // Storage may be unavailable in private mode; the in-memory default remains usable.
    }
    return null
  },
  write(value: string | null) {
    return value ?? ''
  },
}

// Auth is deliberately a module singleton: mounting a second gate must not
// create a second session that can diverge from the queries' bearer token.
const session = useStorage<string | null>(STORAGE_KEY, null, undefined, {
  serializer: tokenSerializer,
  shallow: true,
  writeDefaults: false,
  onError: () => {
    // A blocked/full localStorage must not prevent a current-tab login.
  },
})

const isAuthenticated = computed(() => session.value !== null)

export function useWorkerAuth() {
  const loginPending = shallowRef(false)

  async function login(username: string, password: string): Promise<WorkerLoginResponse> {
    loginPending.value = true
    try {
      const response = await loginToWorker(username, password)
      session.value = response.token
      return response
    } finally {
      loginPending.value = false
    }
  }

  function clearSession() {
    session.value = null
  }

  return { session, isAuthenticated, loginPending, login, clearSession }
}
